import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api-client.config';
import { queryKeys } from '../react-query.config';
import { toast } from 'sonner';

export interface MarketingConsentAsset {
  url: string;
  filename?: string;
  label?: string;
}

export interface MarketingConsentVersion {
  id: string;
  tenantId: string;
  title: string;
  version?: string | null;
  description?: string | null;
  consentText: string;
  headerImageUrl?: string | null;
  assets: MarketingConsentAsset[];
  isDefault: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface VersionsResponse {
  versions: MarketingConsentVersion[];
}

interface VersionResponse {
  version: MarketingConsentVersion;
}

export interface CreateMarketingConsentVersionBody {
  title: string;
  consent_text: string;
  version?: string;
  description?: string;
  header_image_url?: string | null;
  assets?: MarketingConsentAsset[];
  is_default?: boolean;
}

export type UpdateMarketingConsentVersionBody = Partial<CreateMarketingConsentVersionBody>;

const versionsQueryKey = (tenantId: string) =>
  [...queryKeys.tenants.detail(tenantId), 'marketing-consent-versions'] as const;

export const getMarketingConsentVersions = async (tenantId: string): Promise<VersionsResponse> => {
  return (apiClient.get as any)(`/api/admin/tenants/${tenantId}/marketing-consent-versions`);
};

export const createMarketingConsentVersion = async (
  tenantId: string,
  body: CreateMarketingConsentVersionBody
): Promise<VersionResponse> => {
  return (apiClient.post as any)(`/api/admin/tenants/${tenantId}/marketing-consent-versions`, body);
};

export const updateMarketingConsentVersion = async (
  tenantId: string,
  versionId: string,
  body: UpdateMarketingConsentVersionBody
): Promise<VersionResponse> => {
  return (apiClient.put as any)(
    `/api/admin/tenants/${tenantId}/marketing-consent-versions/${versionId}`,
    body
  );
};

export const useGetMarketingConsentVersionsQuery = (tenantId: string | undefined) => {
  return useQuery({
    queryKey: versionsQueryKey(tenantId || ''),
    queryFn: () => getMarketingConsentVersions(tenantId!),
    enabled: !!tenantId,
  });
};

export const useCreateMarketingConsentVersionMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMarketingConsentVersionBody) =>
      createMarketingConsentVersion(tenantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: versionsQueryKey(tenantId) });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create marketing consent version');
    },
  });
};

export const useUpdateMarketingConsentVersionMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      versionId,
      body,
    }: {
      versionId: string;
      body: UpdateMarketingConsentVersionBody;
    }) => updateMarketingConsentVersion(tenantId, versionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: versionsQueryKey(tenantId) });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update marketing consent version');
    },
  });
};

export interface MarketingConsentUserRecord {
  id: string;
  userId: string;
  tenantId: string;
  gameType: string;
  marketingConsentGiven: boolean | null;
  marketingConsentVersionId: string | null;
  marketingConsentGivenAt: string | null;
  createdAt: string;
  updatedAt: string;
  ssoUserId: string | null;
  username: string | null;
  email: string | null;
}

interface MarketingConsentUsersResponse {
  consents: MarketingConsentUserRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const consentsQueryKey = (tenantId: string, page: number, gameType?: string) =>
  [...queryKeys.tenants.detail(tenantId), 'marketing-consent-users', page, gameType] as const;

export const getMarketingConsentUsers = async (
  tenantId: string,
  params: { page?: number; limit?: number; game_type?: string } = {}
): Promise<MarketingConsentUsersResponse> => {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.game_type) searchParams.set('game_type', params.game_type);
  const query = searchParams.toString();
  return (apiClient.get as any)(
    `/api/admin/tenants/${tenantId}/marketing-consent-users${query ? `?${query}` : ''}`
  );
};

export const useGetMarketingConsentUsersQuery = (
  tenantId: string | undefined,
  page = 1,
  gameType?: string
) => {
  return useQuery({
    queryKey: consentsQueryKey(tenantId || '', page, gameType),
    queryFn: () => getMarketingConsentUsers(tenantId!, { page, limit: 50, game_type: gameType }),
    enabled: !!tenantId,
  });
};
